import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import UsersTab from './UsersTab';
import { useAdminAnalytics } from './analytics/useAdminAnalytics';
import { AnalyticsStatus, DateRangeToolbar } from './analytics/AnalyticsStatus';
import SessionMetricsPanel, { RecentActivityGrid } from './analytics/SessionAndActivity';
import {
  DevicesSection,
  EngagementSection,
  GeographicSection,
  TopActiveUsers,
} from './analytics/UsersDetailSections';
import '../../styles/AdminDashboard.css';

export default function AdminUsersPage() {
  const { userTab } = useParams();
  const analytics = useAdminAnalytics({
    title: 'Users',
    subtitle: 'Directory, sessions, geography, devices, and engagement',
  });

  const {
    analyticsData,
    loading,
    refreshing,
    error,
    isAuthenticated,
    lastUpdatedAt,
    growthRangeDraft,
    growthRangeApplied,
    growthRangeError,
    analyticsRangePreset,
    fetchAnalytics,
    applyFunnelPreset,
    handleGrowthRangeDraftChange,
    applyGrowthRange,
    clearGrowthRange,
    deviceStatsData,
  } = analytics;

  if (userTab) return <Navigate to="/admin/users" replace />;

  return (
    <AnalyticsStatus
      loading={loading}
      error={error}
      isAuthenticated={isAuthenticated}
      analyticsData={analyticsData}
      onRetry={() => fetchAnalytics()}
    >
    {analyticsData ? (
      <div className="admin-dashboard">
        {refreshing ? (
          <p className="adminMutedText" style={{ marginTop: 0 }}>Updating…</p>
        ) : null}

        <DateRangeToolbar
          analyticsRangePreset={analyticsRangePreset}
          growthRangeApplied={growthRangeApplied}
          growthRangeDraft={growthRangeDraft}
          growthRangeError={growthRangeError}
          lastUpdatedAt={lastUpdatedAt}
          loading={loading || refreshing}
          onPreset={applyFunnelPreset}
          onDraftChange={handleGrowthRangeDraftChange}
          onApply={applyGrowthRange}
          onClear={clearGrowthRange}
          hint="This UTC window is shared with Growth. It drives average session duration, geography, devices, engagement, and top active users."
        />

        <div className="analytics-section" style={{ marginTop: 16 }}>
          <h2>Session Metrics</h2>
          <SessionMetricsPanel sessions={analyticsData.sessions} variant="full" />
        </div>

        <TopActiveUsers users={analyticsData.sessions?.topActiveUsers || []} />
        <RecentActivityGrid recentActivity={analyticsData.recentActivity} />

        <UsersTab
          analyticsUsers={analyticsData.users || null}
          recentRegistrations={analyticsData.recentActivity?.recentRegistrations || null}
          showRecentRegistrations={false}
        />

        <EngagementSection engagement={analyticsData.engagement} />
        <GeographicSection
          geoData={analyticsData.geography?.countries || []}
          cities={analyticsData.geography?.cities || []}
        />
        <DevicesSection deviceStatsData={deviceStatsData} />
      </div>
    ) : null}
    </AnalyticsStatus>
  );
}
